import { useEffect, useState } from 'react'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Button } from '@/components/ui/button.tsx'
import { ELEMENT_STATUSES, ElementStatus, STEP_STATUSES } from '@/const/const.ts'
import { getActiveRole } from '@/lib/iterations/useIterationActiveRole.ts'
import { cn } from '@/lib/utils.ts'
import { IterationStep } from '@/services/firebase_shared_types.ts'
import { Circle, ChevronDown, LucideLink } from 'lucide-react'
import { useIsUserSuperAdmin } from '@/stores/useStore'
import { MStatusIconBox, ROLE_REGISTRY_STATUS } from './MStatusIconBox.tsx'
import { useSearchParams } from 'react-router-dom'
import { useClipboardCopy } from '@/lib/hooks'
import { useToast } from '@/components/ui/use-toast'

export function MStep({ idx, step, showDetails = true }: MCompactStepProps) {
  const isSuperAdmin = useIsUserSuperAdmin()
  const [isExpanded, setIsExpanded] = useState(false)
  const roleRegistry = getActiveRole(step.role_registry)
  const isActiveRole = roleRegistry?.status === 'waiting_for_message'
  const status = (isActiveRole ? ROLE_REGISTRY_STATUS : step.status) as ElementStatus
  const isDone = status === ELEMENT_STATUSES.DONE
  const detailsTitle = isDone ? 'REPORT' : 'OBJECTIVES'
  const [_, setSearchParams] = useSearchParams()
  const { copy: copyUrl } = useClipboardCopy()
  const { toast } = useToast()

  const isRunninng = step.status === ELEMENT_STATUSES.RUNNING

  useEffect(() => {
    // if running, we want to expanded. If not running, we want to set it to collapsed.
    setIsExpanded(isRunninng)
  }, [isRunninng, setIsExpanded])

  if (!isSuperAdmin && step.status === ELEMENT_STATUSES.AWAITING) {
    return null
  }

  const updateUrl = () => {
    setSearchParams(
      prevParams => {
        const newParams = new URLSearchParams(prevParams)
        if (step?.index) {
          newParams.set('step', step.index)
        } else {
          newParams.delete('step')
        }
        newParams.set('element', step.elementIndex)
        newParams.delete('action')
        return newParams
      },
      { replace: false }
    )
  }

  const handleDeepLinkAction = () => {
    updateUrl()

    copyUrl(
      window.location.href,
      () =>
        toast({
          title: 'Step link copied to clipboard! 🎉',
        }),
      () =>
        toast({
          variant: 'destructive',
          title: 'Failed to copy step link to clipboard 😔',
          description: 'Check console for details and try again.',
        })
    )
  }

  return (
    <Collapsible
      open={isExpanded}
      onOpenChange={setIsExpanded}
      className={cn(
        'w-full animate-appear rounded border text-base-foreground transition-colors',
        isExpanded || !showDetails ? 'breder-border bg-white' : 'bg-transparents border-transparent'
      )}
    >
      <div
        className="group flex w-full cursor-pointer flex-row items-center gap-4 p-1"
        onClick={updateUrl}
      >
        <MStatusIconBox status={status} />
        <div className="flex flex-1 items-center justify-between text-sm">
          <h4 className="">
            <span
              className={cn(
                'font-pp-supply-sans',
                status === ELEMENT_STATUSES.FAILED ? 'text-red-400' : 'text-muted-foreground'
              )}
            >
              {step.elementIndex}.{step.index}.
            </span>{' '}
            <span
              className={cn(
                'text-sm font-medium',
                status === ELEMENT_STATUSES.FAILED
                  ? 'text-red-500'
                  : isExpanded || status === STEP_STATUSES.RUNNING || !showDetails
                    ? 'text-base-foreground'
                    : 'text-stone-500'
              )}
            >
              {step.name}
            </span>
          </h4>

          <div className="flex h-8 items-center justify-center rounded-md bg-white/0">
            {isSuperAdmin && (
              <Button
                variant="ghost"
                className="px-2 opacity-0 transition-opacity group-hover:opacity-100"
                onClick={handleDeepLinkAction}
              >
                <LucideLink className="size-4" />
              </Button>
            )}
            {showDetails && step.description && (
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="icon" className="ml-auto shrink-0">
                  <ChevronDown
                    className={cn(
                      'size-4 text-muted-foreground transition-transform',
                      isExpanded && 'rotate-180'
                    )}
                  />
                </Button>
              </CollapsibleTrigger>
            )}
          </div>
        </div>
      </div>

      {showDetails && (
        <CollapsibleContent>
          <div className="px-5 py-4 text-sm text-base-foreground">
            <p>{step.description}</p>
            {isDone && step.output && (
              <>
                <DetailsHeader title={detailsTitle} />
                <Report content={step.output} />
              </>
            )}
          </div>
        </CollapsibleContent>
      )}
    </Collapsible>
  )
}
function DetailsHeader({ title }: { title: string }) {
  return (
    <div className="mt-5 flex items-center gap-2 font-dm-mono text-xs text-stone-900">
      {title} <div className="grow border-b border-border" />
    </div>
  )
}

function Objectives({ objectives }: { objectives: string[] }) {
  return (
    <ul className="mt-2 space-y-1 text-sm">
      {objectives.map(objective => (
        <li key={objective} className="flex items-center gap-2">
          <Circle className="size-4 text-border" />
          {objective}
        </li>
      ))}
    </ul>
  )
}

type PydanticModel = {
  _type: 'pydantic'
  class: 'StepOutput'
  value: string // stringified json
}
function Report({ content }: { content: string | PydanticModel }) {
  if (content === null) {
    return null
  }
  let report: string
  if (typeof content === 'string') {
    report = content // doesn't really happen
  } else {
    // it's a serialized pydantic model
    const output = JSON.parse(content.value)
    report = output.report
  }

  return <div className="mt-2 text-sm">{report}</div>
}

type MCompactStepProps = {
  idx: number
  step: IterationStep
  showDetails?: boolean
}
